import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, arrayUnion, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

const COUNTRIES = [
    "Belgium", "Netherlands", "France", "Germany", "Luxembourg", "United Kingdom", "United States", "Other"
];

export default function ConsultantDatabaseView({ consultantDb, loading, logAction, isReadOnly, initialSelectedId, currentUserEmail, employees }) {
  // --- STATE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'my'
  const [activeModalTab, setActiveModalTab] = useState('profile'); // 'profile' or 'scorecard' (For the Edit Modal)
  
  // Selection for Detail View
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [activeDetailTab, setActiveDetailTab] = useState('overview'); // 'overview', 'applications', 'communications'

  // Application History State (Option 3)
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);

  // Image Upload
  const [profileImage, setProfileImage] = useState(null);

  // Form State
  const initialForm = {
    firstName: '', lastName: '', city: '', country: 'Belgium', 
    email: '', phone: '', 
    dateOfBirth: '', careerStartDate: '',
    softSkills: '', hardSkills: '', 
    // NEW FIELDS
    pools: '', // Talent Pools / Tags
    status: 'Available', 
    availabilityDate: '',
    externalAvailabilityDate: '', // For "Sniper" logic (working elsewhere)
    lastContactDate: '', // For "Pulse Check" logic
    nextCheckInDate: '', // For "Snooze" logic
    imageFile: '',
    spocEmail: '', spocName: '', spocId: '', // Added spocId
    outreachStatus: 'Need to Contact', 
    availabilityAcknowledged: false,
    
    notesLog: [],
    resumes: [],
    scorecards: [],
    communicationLog: [] // NEW: Stores sent emails/linkedin messages
  };
  const [formData, setFormData] = useState(initialForm);
  
  // Scorecard Input State
  const [newScorecard, setNewScorecard] = useState({ technical: 5, culture: 5, notes: '' });

  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Handle deep link
  useEffect(() => {
      if (initialSelectedId && consultantDb.length > 0) {
          const found = consultantDb.find(c => c.id === initialSelectedId);
          if (found) setSelectedConsultant(found);
      }
  }, [initialSelectedId, consultantDb]);

  // --- NEW: FETCH APPLICATIONS HISTORY ---
  useEffect(() => {
      if (selectedConsultant) {
          const fetchHistory = async () => {
              setLoadingApps(true);
              try {
                  // Query the top-level kanban_items collection for any card linked to this consultant
                  const q = query(
                      collection(db, "kanban_items"), 
                      where("relatedId", "==", selectedConsultant.id)
                  );
                  const snap = await getDocs(q);
                  const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                  
                  // Sort by creation date (newest first)
                  items.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                  
                  setApplications(items);
              } catch (err) {
                  console.error("Error fetching application history:", err);
              } finally {
                  setLoadingApps(false);
              }
          };
          fetchHistory();
      } else {
          setApplications([]);
      }
  }, [selectedConsultant]);

  // --- HELPER: CALCULATIONS ---
  const calculateDiffInYears = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const diff = Date.now() - date.getTime();
      return Math.abs(new Date(diff).getUTCFullYear() - 1970);
  };

  const getAvailabilityCountdown = (dateString) => {
      if (!dateString) return { days: 0, label: 'No Date' };
      const target = new Date(dateString);
      const today = new Date();
      const diffTime = target - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      let label = '';
      if (diffDays < 0) label = 'Overdue';
      else if (diffDays === 0) label = 'Today';
      else label = `${diffDays} Days`;

      return { days: diffDays, label };
  };

  // --- HANDLERS: SPOC ---
  const handleAssignSpoc = () => {
      if (!currentUserEmail) return;
      const me = employees.find(e => e.email === currentUserEmail);
      setFormData(prev => ({
          ...prev,
          spocEmail: currentUserEmail,
          spocName: me ? me.name : currentUserEmail,
          spocId: me ? me.id : '', // Save the ID for Kanban assignment
          outreachStatus: 'Need to Contact' 
      }));
  };

  // --- HANDLERS: UPLOAD/PARSE ---
  const handleProfileImageChange = (e) => {
      if (e.target.files[0]) setProfileImage(e.target.files[0]);
  };

  const handleResumeUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setIsParsing(true);
      try {
          const storageRef = ref(storage, `resumes/${Date.now()}_${file.name}`);
          const uploadResult = await uploadBytes(storageRef, file);
          const downloadUrl = await getDownloadURL(storageRef);
          
          const newResumeFile = {
              name: file.name,
              url: downloadUrl,
              date: new Date().toISOString()
          };

          const functions = getFunctions(getApp(), 'europe-west1');
          const parseResumeFn = httpsCallable(functions, 'parseResume');
          const gcsUrl = `gs://${uploadResult.metadata.bucket}/${uploadResult.metadata.fullPath}`;
          const response = await parseResumeFn({ gcsUrl, mimeType: file.type });
          const { data, success } = response.data;

          if (success) {
              setFormData(prev => ({
                  ...prev,
                  email: data.email || prev.email,
                  phone: data.phone || prev.phone,
                  hardSkills: data.hardSkills || prev.hardSkills,
                  softSkills: data.softSkills || prev.softSkills,
                  resumes: [...(prev.resumes || []), newResumeFile]
              }));
              alert("Resume parsed and attached!");
          } else {
              alert("Resume uploaded (parsing skipped).");
              setFormData(prev => ({
                  ...prev,
                  resumes: [...(prev.resumes || []), newResumeFile]
              }));
          }

      } catch (err) {
          console.error("Resume Error:", err);
          alert("Error processing resume.");
      } finally {
          setIsParsing(false);
      }
  };

  const [error, setError] = useState(null);

  // --- HANDLERS: CRUD ---
  const handleSave = async (e) => {
    e.preventDefault();
    if(isReadOnly) {
        setError("You are in Read-Only mode. You cannot make changes.");
        return;
    }
    setIsUploading(true);
    setError(null);

    try {
      // 1. Determine Image URL
      let imageUrl = formData.imageFile || ""; 
      
      if (profileImage) {
          const imgRef = ref(storage, `consultant-profiles/${Date.now()}_${profileImage.name}`);
          await uploadBytes(imgRef, profileImage);
          imageUrl = await getDownloadURL(imgRef);
      }

      // 2. Construct Final Data
      const finalData = { ...formData, imageFile: imageUrl };

      // Sanitize data
      Object.keys(finalData).forEach(key => {
          if (finalData[key] === undefined) {
              finalData[key] = ""; 
          }
      });
      
      if (isEditing) {
        await updateDoc(doc(db, "consultant_db", editingId), finalData);
        if(selectedConsultant && selectedConsultant.id === editingId) {
            setSelectedConsultant({...finalData, id: editingId});
        }
        logAction("Update Candidate", `Updated ${finalData.firstName} ${finalData.lastName}`);
      } else {
        await addDoc(collection(db, "consultant_db"), finalData);
        logAction("Create Candidate", `Created ${finalData.firstName} ${finalData.lastName}`);
      }
      setShowModal(false);
    } catch(err) { 
        console.error("Save Error:", err); 
        alert(`Error saving profile: ${err.message}`); 
    } finally { 
        setIsUploading(false); 
    }
  };

  const handleDelete = async () => {
      if(isReadOnly) return;
      if (!window.confirm("Permanently delete this consultant?")) return;
      try {
          const id = isEditing ? editingId : selectedConsultant.id;
          await deleteDoc(doc(db, "consultant_db", id));
          logAction("Delete Candidate", `Deleted candidate ID: ${id}`);
          setShowModal(false);
          setSelectedConsultant(null);
      } catch(err) { console.error(err); }
  };

  const addNote = async () => {
      if(!noteText || !selectedConsultant) return;
      const note = { text: noteText, date: new Date().toISOString(), author: 'User' };
      try {
          await updateDoc(doc(db, "consultant_db", selectedConsultant.id), {
              notesLog: arrayUnion(note)
          });
          setNoteText('');
          setSelectedConsultant(prev => ({...prev, notesLog: [...(prev.notesLog || []), note]}));
      } catch(err) { console.error(err); }
  };

  // --- SCORECARD HANDLER ---
  const handleAddScorecard = () => {
      const entry = {
          ...newScorecard,
          date: new Date().toISOString(),
          interviewer: currentUserEmail || 'Admin'
      };
      setFormData(prev => ({
          ...prev,
          scorecards: [...(prev.scorecards || []), entry]
      }));
      setNewScorecard({ technical: 5, culture: 5, notes: '' });
      alert("Scorecard added to profile (Click Save to persist changes)");
  };

  const openModal = (c = null) => {
      setActiveModalTab('profile'); // Reset tab
      if(c) { 
          setFormData({
            firstName: c.firstName || '', lastName: c.lastName || '', 
            city: c.city || '', country: c.country || 'Belgium', 
            email: c.email || '', phone: c.phone || '', 
            dateOfBirth: c.dateOfBirth || '', careerStartDate: c.careerStartDate || '',
            softSkills: c.softSkills || '', hardSkills: c.hardSkills || '', 
            pools: c.pools || '', // Load pools
            status: c.status || 'Available', 
            availabilityDate: c.availabilityDate || '',
            // New Fields Mapping
            externalAvailabilityDate: c.externalAvailabilityDate || '',
            lastContactDate: c.lastContactDate || '',
            nextCheckInDate: c.nextCheckInDate || '',
            
            imageFile: c.imageFile || '',
            spocEmail: c.spocEmail || '', spocName: c.spocName || '', spocId: c.spocId || '', // Load SPOC ID
            outreachStatus: c.outreachStatus || 'Need to Contact', 
            availabilityAcknowledged: c.availabilityAcknowledged || false,
            notesLog: c.notesLog || [],
            resumes: c.resumes || [],
            scorecards: c.scorecards || [],
            communicationLog: c.communicationLog || []
          });
          setEditingId(c.id); 
          setIsEditing(true); 
      } else { 
          setFormData(initialForm); 
          setIsEditing(false); 
      }
      setProfileImage(null);
      setShowModal(true);
  };

  // --- FILTERING ---
  const filteredDb = consultantDb.filter(c => {
      const searchStr = ((c.firstName || "") + (c.lastName || "") + (c.hardSkills || "") + (c.pools || "")).toLowerCase();
      const matchSearch = searchStr.includes(searchTerm.toLowerCase());
      const matchSpoc = viewMode === 'my' ? c.spocEmail === currentUserEmail : true;
      return matchSearch && matchSpoc;
  });

  // --- LOG CONTACT (RESET TIMER) ---
  const handleLogContact = async () => {
      if(!selectedConsultant || isReadOnly) return;
      
      const now = new Date().toISOString();
      try {
          // Update DB
          await updateDoc(doc(db, "consultant_db", selectedConsultant.id), {
              lastContactDate: now,
              outreachStatus: 'Reached Out'
          });
          
          // Update Local State
          setSelectedConsultant(prev => ({
              ...prev,
              lastContactDate: now,
              outreachStatus: 'Reached Out'
          }));
          
          logAction("Log Contact", `Reset pulse check timer for ${selectedConsultant.firstName}`);
          alert("Contact logged. Bench timer reset.");
      } catch(err) {
          console.error(err);
          alert("Error logging contact.");
      }
  };

  // --- RENDER: DETAIL VIEW ---
  if (selectedConsultant) {
      const countdown = getAvailabilityCountdown(selectedConsultant.availabilityDate);
      const isUrgent = countdown.days <= 30 && countdown.days > -5;

      return (
          <div className="container-fluid p-0">
              <div className="d-flex justify-content-between align-items-center mb-3">
                  <button className="btn btn-outline-secondary" onClick={() => setSelectedConsultant(null)}>
                      <i className="bi bi-arrow-left me-2"></i> Back to List
                  </button>
                  <div className="d-flex gap-2">
                      {!isReadOnly && <button className="btn btn-outline-primary" onClick={() => openModal(selectedConsultant)}>Edit Profile</button>}
                      {!isReadOnly && <button className="btn btn-outline-danger" onClick={handleDelete}>Delete</button>}
                  </div>
              </div>

              <div className="row g-4">
                  <div className="col-lg-8">
                      <div className="card bg-card border-theme h-100 shadow-sm">
                          <div className="card-body">
                              {/* HEADER INFO */}
                              <div className="d-flex align-items-start mb-4">
                                  {selectedConsultant.imageFile ? (
                                      <img 
                                        src={selectedConsultant.imageFile} 
                                        alt="Profile" 
                                        className="rounded-circle object-fit-cover border border-theme me-4" 
                                        style={{width: 100, height: 100}} 
                                      />
                                  ) : (
                                      <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold me-4" style={{width: 100, height: 100, fontSize: 32}}>
                                          {selectedConsultant.firstName ? selectedConsultant.firstName.charAt(0) : '?'}
                                      </div>
                                  )}
                                  <div className="flex-grow-1">
                                      <h2 className="text-main mb-1">{selectedConsultant.firstName} {selectedConsultant.lastName}</h2>
                                      <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                                          <span className={`badge ${selectedConsultant.status === 'Available' ? 'bg-success' : (selectedConsultant.status === 'Placed' ? 'bg-primary' : 'bg-warning text-dark')}`}>
                                              {selectedConsultant.status}
                                          </span>
                                          {selectedConsultant.availabilityDate && (
                                              <span className={`badge border ${isUrgent ? 'border-danger text-danger' : 'border-secondary text-muted-theme'}`}>
                                                  Avail: {selectedConsultant.availabilityDate} ({countdown.label})
                                              </span>
                                          )}
                                          {selectedConsultant.pools && selectedConsultant.pools.split(',').map(tag => (
                                              <span key={tag} className="badge bg-info text-dark">{tag.trim()}</span>
                                          ))}
                                      </div>
                                      <div className="text-muted-theme small">
                                          <i className="bi bi-geo-alt me-1"></i> {selectedConsultant.city}, {selectedConsultant.country}
                                      </div>
                                  </div>
                              </div>

                              {/* DETAIL TABS (Overview / Applications) */}
                              <ul className="nav nav-tabs border-theme mb-3">
                                  <li className="nav-item">
                                      <button 
                                          className={`nav-link ${activeDetailTab === 'overview' ? 'active bg-input text-primary border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} 
                                          onClick={() => setActiveDetailTab('overview')}
                                      >
                                          Overview
                                      </button>
                                  </li>
                                  <li className="nav-item">
                                      <button 
                                          className={`nav-link ${activeDetailTab === 'applications' ? 'active bg-input text-primary border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} 
                                          onClick={() => setActiveDetailTab('applications')}
                                      >
                                          Applications <span className="badge bg-secondary ms-1">{applications.length}</span>
                                      </button>
                                  </li>
                                  <li className="nav-item">
                                      <button 
                                          className={`nav-link ${activeDetailTab === 'communications' ? 'active bg-input text-primary border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} 
                                          onClick={() => setActiveDetailTab('communications')}
                                      >
                                          Communications
                                      </button>
                                  </li>
                              </ul>

                              {/* TAB CONTENT: OVERVIEW */}
                              {activeDetailTab === 'overview' && (
                                <>
                                    {/* BENCH / NURTURE STATUS */}
                                    <div className="bg-input border border-theme rounded p-3 mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="text-main mb-0 fw-bold">Bench Management</h6>
                                            {!isReadOnly && (
                                                <button className="btn btn-sm btn-outline-success" onClick={handleLogContact}>
                                                    <i className="bi bi-check2-circle me-2"></i> Log Contact
                                                </button>
                                            )}
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <small className="text-muted-theme d-block">Last Contacted</small>
                                                <div className="fw-bold text-main">
                                                    {selectedConsultant.lastContactDate 
                                                        ? new Date(selectedConsultant.lastContactDate).toLocaleDateString() 
                                                        : 'Never'}
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <small className="text-muted-theme d-block">Next Check-in</small>
                                                <div className="fw-bold text-accent">
                                                    {selectedConsultant.nextCheckInDate 
                                                        ? new Date(selectedConsultant.nextCheckInDate).toLocaleDateString() 
                                                        : 'Auto (6 Months)'}
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <small className="text-muted-theme d-block">External Availability</small>
                                                <div className="fw-bold text-main">
                                                    {selectedConsultant.externalAvailabilityDate 
                                                        ? selectedConsultant.externalAvailabilityDate 
                                                        : '-'}
                                                </div>
                                                {selectedConsultant.externalAvailabilityDate && <small className="text-muted fst-italic">Triggers "Sniper" alert</small>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* SPOC & OUTREACH */}
                                    <div className="bg-input border border-theme rounded p-3 mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="text-main mb-0">Ownership</h6>
                                            {selectedConsultant.spocName ? (
                                                <span className="badge bg-info text-dark">{selectedConsultant.spocName}</span>
                                            ) : (
                                                <span className="badge bg-secondary">Unassigned</span>
                                            )}
                                        </div>
                                        <div className="row g-3 align-items-end">
                                            <div className="col-md-4">
                                                <small className="text-muted-theme d-block mb-1">Outreach Status</small>
                                                <div className={`fw-bold ${selectedConsultant.outreachStatus === 'Replied' ? 'text-success' : 'text-main'}`}>
                                                    {selectedConsultant.outreachStatus || 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CONTACT & EXP */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-6"><small className="text-muted-theme">Email</small><div className="text-main fw-bold">{selectedConsultant.email}</div></div>
                                        <div className="col-md-6"><small className="text-muted-theme">Phone</small><div className="text-main fw-bold">{selectedConsultant.phone}</div></div>
                                        <div className="col-md-6"><small className="text-muted-theme">Experience</small><div className="text-main fw-bold">{selectedConsultant.careerStartDate ? `${calculateDiffInYears(selectedConsultant.careerStartDate)} Years` : '-'}</div></div>
                                        <div className="col-md-6"><small className="text-muted-theme">DOB</small><div className="text-main fw-bold">{selectedConsultant.dateOfBirth || '-'}</div></div>
                                    </div>
                                    
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <h6 className="text-primary">Skills</h6>
                                            <p className="text-main">{selectedConsultant.hardSkills}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <h6 className="text-primary">Soft Skills</h6>
                                            <p className="text-main">{selectedConsultant.softSkills}</p>
                                        </div>
                                    </div>

                                    {/* INTERVIEW SCORECARDS */}
                                    {selectedConsultant.scorecards && selectedConsultant.scorecards.length > 0 && (
                                        <div className="mt-4">
                                            <h6 className="text-primary border-bottom border-theme pb-2 mb-3">Interview Scorecards</h6>
                                            <div className="row g-3">
                                                {selectedConsultant.scorecards.map((sc, idx) => (
                                                    <div key={idx} className="col-md-6">
                                                        <div className="bg-input p-3 rounded border border-theme h-100">
                                                            <div className="d-flex justify-content-between mb-2 small text-muted-theme">
                                                                <span>{new Date(sc.date).toLocaleDateString()}</span>
                                                                <span>{sc.interviewer}</span>
                                                            </div>
                                                            <div className="mb-2">
                                                                <span className="badge bg-primary me-2">Tech: {sc.technical}/5</span>
                                                                <span className="badge bg-info text-dark">Culture: {sc.culture}/5</span>
                                                            </div>
                                                            <p className="small text-main mb-0 fst-italic">"{sc.notes}"</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <h6 className="text-primary mt-4">Documents</h6>
                                    {selectedConsultant.resumes && selectedConsultant.resumes.length > 0 ? (
                                        <ul className="list-group">
                                            {selectedConsultant.resumes.map((res, idx) => (
                                                <li key={idx} className="list-group-item bg-input border-theme d-flex justify-content-between align-items-center">
                                                    <span className="text-main"><i className="bi bi-file-earmark-pdf text-danger me-2"></i> {res.name}</span>
                                                    <a href={res.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">Download</a>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <p className="text-muted-theme small fst-italic">No documents.</p>}
                                </>
                              )}

                              {/* TAB CONTENT: APPLICATIONS HISTORY (OPTION 3) */}
                              {activeDetailTab === 'applications' && (
                                  <div className="py-2">
                                      {loadingApps ? <div className="text-center text-muted-theme py-4">Loading history...</div> : (
                                          applications.length > 0 ? (
                                              <div className="list-group">
                                                  {applications.map(app => {
                                                      const isActive = !['Rejected', 'Archived', 'Done'].includes(app.status);
                                                      const dateStr = app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '-';
                                                      
                                                      return (
                                                          <div key={app.id} className={`list-group-item bg-input border-theme mb-2 rounded ${isActive ? 'border-start border-primary border-3' : 'opacity-75'}`}>
                                                              <div className="d-flex justify-content-between align-items-center mb-1">
                                                                  <h6 className="mb-0 fw-bold text-main">{app.title}</h6>
                                                                  <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>{app.status}</span>
                                                              </div>
                                                              <p className="small text-muted-theme mb-2">{app.subtitle}</p>
                                                              <div className="d-flex justify-content-between align-items-center border-top border-secondary pt-2 mt-2">
                                                                  <small className="text-muted-theme"><i className="bi bi-calendar3 me-1"></i> {dateStr}</small>
                                                                  <small className="text-muted-theme"><i className="bi bi-person me-1"></i> {app.assigneeName}</small>
                                                              </div>
                                                          </div>
                                                      );
                                                  })}
                                              </div>
                                          ) : (
                                              <div className="text-center py-5 text-muted-theme">
                                                  <i className="bi bi-folder-x fs-1 d-block mb-3"></i>
                                                  <p>No application history found for this consultant.</p>
                                              </div>
                                          )
                                      )}
                                  </div>
                              )}

                              {/* NEW: COMMUNICATIONS TAB */}
                              {activeDetailTab === 'communications' && (
                                  <div className="p-2">
                                      {selectedConsultant.communicationLog && selectedConsultant.communicationLog.length > 0 ? (
                                          <div className="list-group">
                                              {[...selectedConsultant.communicationLog].reverse().map((log, idx) => (
                                                  <div key={idx} className="list-group-item bg-input border-theme mb-2 rounded">
                                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                                          <div>
                                                              <i className={`bi ${log.type === 'Mail' ? 'bi-envelope text-primary' : 'bi-linkedin text-info'} me-2`}></i>
                                                              <span className="fw-bold text-main">{log.type} Sent</span>
                                                          </div>
                                                          <small className="text-muted-theme">{new Date(log.date).toLocaleDateString()}</small>
                                                      </div>
                                                      <p className="small text-muted-theme mb-0">
                                                          For Vacancy: <span className="text-main fw-bold">{log.vacancyTitle}</span>
                                                      </p>
                                                  </div>
                                              ))}
                                          </div>
                                      ) : (
                                          <div className="text-center py-5 text-muted-theme">
                                              <i className="bi bi-chat-square-text fs-1 d-block mb-3"></i>
                                              <p>No communication history found.</p>
                                          </div>
                                      )}
                                  </div>
                              )}
                          </div>
                      </div>
                  </div>

                  {/* NOTES SIDEBAR */}
                  <div className="col-lg-4">
                      <div className="card bg-card border-theme h-100 shadow-sm">
                          <div className="card-header border-theme"><h5 className="text-main mb-0">Internal Notes</h5></div>
                          <div className="card-body d-flex flex-column">
                              <div className="flex-grow-1 overflow-auto mb-3" style={{maxHeight: '500px'}}>
                                  {selectedConsultant.notesLog && selectedConsultant.notesLog.length > 0 ? selectedConsultant.notesLog.map((n, i) => (
                                      <div key={i} className="bg-input p-3 rounded mb-2 border border-theme">
                                          <div className="d-flex justify-content-between small text-muted-theme mb-1">
                                              <span>{n.author}</span>
                                              <span>{n.date.split('T')[0]}</span>
                                          </div>
                                          <span className="text-main">{n.text}</span>
                                      </div>
                                  )) : <p className="text-muted-theme text-center mt-5">No notes yet.</p>}
                              </div>
                              <div className="input-group">
                                  <input className="form-control" placeholder="Add note..." value={noteText} onChange={e=>setNoteText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} />
                                  <button className="btn btn-primary" onClick={addNote}><i className="bi bi-send"></i></button>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      );
  }

  // --- RENDER: LIST VIEW ---
  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
            <h2 className="fw-bold text-main">Consultant Database</h2>
            <p className="text-muted-theme">Talent pool and candidate tracking.</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
            <div className="btn-group me-2">
                <button className={`btn ${viewMode === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('all')}>All Consultants</button>
                <button className={`btn ${viewMode === 'my' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('my')}>My Consultants</button>
            </div>
            {!isReadOnly && <button className="btn btn-success" onClick={() => openModal()}><i className="bi bi-plus-lg"></i> Add</button>}
        </div>
      </div>

      <input className="form-control mb-4" placeholder="Search by name, skills, tags..." onChange={e=>setSearchTerm(e.target.value)} />

      <div className="table-responsive bg-card border border-theme rounded shadow-sm">
          <table className="table table-hover mb-0 text-main align-middle">
              <thead className="bg-input">
                  <tr>
                      <th className="bg-card border-theme">Name</th>
                      <th className="bg-card border-theme">Status</th>
                      <th className="bg-card border-theme">Pools / Tags</th>
                      <th className="bg-card border-theme">Availability</th>
                      <th className="bg-card border-theme">SPOC</th>
                      <th className="bg-card border-theme text-end">Actions</th>
                  </tr>
              </thead>
              <tbody>
                  {filteredDb.map(c => {
                      const countdown = getAvailabilityCountdown(c.availabilityDate);
                      const isUrgent = countdown.days <= 30 && countdown.days > -100;
                      return (
                          <tr key={c.id} style={{cursor: 'pointer'}} onClick={() => { setSelectedConsultant(c); setActiveDetailTab('overview'); }}>
                              <td className="bg-card border-theme fw-bold">
                                  <div className="d-flex align-items-center">
                                      {c.imageFile ? (
                                          <img src={c.imageFile} className="rounded-circle me-2" style={{width: 32, height: 32, objectFit:'cover'}} alt="Avatar"/>
                                      ) : (
                                          <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold me-2" style={{width: 32, height: 32, fontSize: 12}}>
                                              {c.firstName ? c.firstName.charAt(0) : '?'}
                                          </div>
                                      )}
                                      {c.firstName} {c.lastName}
                                  </div>
                              </td>
                              <td className="bg-card border-theme">
                                  <span className={`badge ${c.status === 'Available' ? 'bg-success' : (c.status === 'Placed' ? 'bg-primary' : 'bg-warning text-dark')}`}>
                                      {c.status}
                                  </span>
                              </td>
                              <td className="bg-card border-theme">
                                  {c.pools ? c.pools.split(',').slice(0,2).map(p=><span key={p} className="badge bg-secondary me-1">{p.trim()}</span>) : '-'}
                              </td>
                              <td className="bg-card border-theme">
                                  {c.availabilityDate ? (
                                      <span className={isUrgent ? 'text-danger fw-bold' : ''}>
                                          {c.availabilityDate} {isUrgent && <i className="bi bi-exclamation-circle ms-1"></i>}
                                      </span>
                                  ) : '-'}
                              </td>
                              <td className="bg-card border-theme text-muted-theme small">
                                  {c.spocName || 'Unassigned'}
                              </td>
                              <td className="bg-card border-theme text-end">
                                  <button className="btn btn-sm btn-outline-secondary" onClick={(e) => {e.stopPropagation(); openModal(c);}}>Edit</button>
                              </td>
                          </tr>
                      );
                  })}
                  {filteredDb.length === 0 && (
                      <tr><td colSpan="6" className="text-center py-4 text-muted-theme">No consultants found.</td></tr>
                  )}
              </tbody>
          </table>
      </div>

      {/* MODAL: ADD / EDIT */}
      {showModal && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto'}}>
              <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content">
                      <div className="modal-header border-theme">
                          <h5 className="modal-title text-main">{isEditing ? 'Edit Profile' : 'New Candidate'}</h5>
                          <button className="btn-close" onClick={()=>setShowModal(false)}></button>
                      </div>
                      
                      {/* MODAL TABS */}
                      <ul className="nav nav-tabs border-theme px-3 pt-2">
                          <li className="nav-item">
                              <button 
                                  className={`nav-link ${activeModalTab === 'profile' ? 'active bg-card text-primary' : 'text-muted-theme'}`} 
                                  onClick={() => setActiveModalTab('profile')}
                              >
                                  Profile Info
                              </button>
                          </li>
                          <li className="nav-item">
                              <button 
                                  className={`nav-link ${activeModalTab === 'scorecard' ? 'active bg-card text-primary' : 'text-muted-theme'}`} 
                                  onClick={() => setActiveModalTab('scorecard')}
                              >
                                  Interview Scorecard
                              </button>
                          </li>
                      </ul>

                      <div className="modal-body">
                          {error && <div className="alert alert-danger mb-3"><i className="bi bi-exclamation-circle-fill me-2"></i>{error}</div>}
                          <form onSubmit={handleSave}>
                              
                              {/* --- TAB: PROFILE --- */}
                              {activeModalTab === 'profile' && (
                                <>
                                    {/* PARSING & UPLOAD */}
                                    <div className="mb-4 p-3 bg-input border border-theme rounded text-center">
                                        <div className="d-flex justify-content-center gap-3">
                                            <label className={`btn btn-outline-primary ${isParsing ? 'disabled' : ''}`}>
                                                <i className="bi bi-magic me-2"></i> {isParsing ? 'Analyzing...' : 'Parse Resume'}
                                                <input type="file" hidden accept=".pdf,.docx" onChange={handleResumeUpload} disabled={isParsing} />
                                            </label>
                                        </div>
                                    </div>

                                    {/* SPOC ASSIGNMENT */}
                                    <div className="alert alert-secondary d-flex justify-content-between align-items-center p-2 mb-4">
                                        <div>
                                            <strong>SPOC: </strong> {formData.spocName || 'Unassigned'}
                                        </div>
                                        {(!formData.spocEmail || formData.spocEmail !== currentUserEmail) && (
                                            <button type="button" className="btn btn-sm btn-primary" onClick={handleAssignSpoc}>Assign Me</button>
                                        )}
                                    </div>

                                    <div className="row g-3">
                                        {/* BASIC INFO */}
                                        <div className="col-6"><label className="small text-muted-theme">First Name</label><input className="form-control" value={formData.firstName} onChange={e=>setFormData({...formData, firstName: e.target.value})} required/></div>
                                        <div className="col-6"><label className="small text-muted-theme">Last Name</label><input className="form-control" value={formData.lastName} onChange={e=>setFormData({...formData, lastName: e.target.value})} required/></div>
                                        
                                        {/* STATUS & DATE */}
                                        <div className="col-4">
                                            <label className="small text-muted-theme">Status</label>
                                            <select className="form-select" value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})}>
                                                <option>Available</option>
                                                <option>Placed</option>
                                                <option>Busy</option>
                                                <option>Soon</option>
                                            </select>
                                        </div>
                                        <div className="col-4">
                                            <label className="small text-muted-theme">Availability Date</label>
                                            <input type="date" className="form-control" value={formData.availabilityDate} onChange={e=>setFormData({...formData, availabilityDate: e.target.value})}/>
                                        </div>
                                        <div className="col-4">
                                            <label className="small text-muted-theme">Outreach Status</label>
                                            <select className="form-select" value={formData.outreachStatus} onChange={e=>setFormData({...formData, outreachStatus: e.target.value})}>
                                                <option>Need to Contact</option><option>Reached Out</option><option>Replied</option><option>Not Interested</option>
                                            </select>
                                        </div>

                                        {/* NEW: EXTERNAL AVAILABILITY */}
                                        <div className="col-12 bg-input p-2 rounded border border-theme">
                                            <label className="small text-main fw-bold">External Availability (If working elsewhere)</label>
                                            <input 
                                                type="date" 
                                                className="form-control" 
                                                value={formData.externalAvailabilityDate} 
                                                onChange={e => setFormData({...formData, externalAvailabilityDate: e.target.value})} 
                                            />
                                            <small className="text-muted-theme">Sets trigger for "Contract Ending Soon" alert.</small>
                                        </div>
                                        
                                        {/* TALENT POOLS (TAGS) */}
                                        <div className="col-12">
                                            <label className="small text-main fw-bold">Talent Pools / Tags</label>
                                            <input 
                                                className="form-control" 
                                                placeholder="e.g. Java Experts, Freelance, Immediate Start" 
                                                value={formData.pools || ''} 
                                                onChange={e => setFormData({...formData, pools: e.target.value})} 
                                            />
                                            <small className="text-muted-theme">Comma separated values.</small>
                                        </div>

                                        <div className="col-6"><label className="small text-muted-theme">Email</label><input className="form-control" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})}/></div>
                                        <div className="col-6"><label className="small text-muted-theme">Phone</label><input className="form-control" value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})}/></div>
                                        
                                        {/* DATES */}
                                        <div className="col-6"><label className="small text-muted-theme">Date of Birth</label><input type="date" className="form-control" value={formData.dateOfBirth} onChange={e=>setFormData({...formData, dateOfBirth: e.target.value})}/></div>
                                        <div className="col-6"><label className="small text-muted-theme">Career Start Date</label><input type="date" className="form-control" value={formData.careerStartDate} onChange={e=>setFormData({...formData, careerStartDate: e.target.value})}/></div>
                                        
                                        <div className="col-6"><label className="small text-muted-theme">City</label><input className="form-control" value={formData.city} onChange={e=>setFormData({...formData, city: e.target.value})}/></div>
                                        <div className="col-6">
                                            <label className="small text-muted-theme">Country</label>
                                            <select className="form-select" value={formData.country} onChange={e=>setFormData({...formData, country: e.target.value})}>
                                                <option value="">Select...</option>
                                                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>

                                        <div className="col-12"><label className="small text-muted-theme">Hard Skills</label><textarea className="form-control" rows="2" value={formData.hardSkills} onChange={e=>setFormData({...formData, hardSkills: e.target.value})}/></div>
                                        <div className="col-12"><label className="small text-muted-theme">Soft Skills</label><textarea className="form-control" rows="2" value={formData.softSkills} onChange={e=>setFormData({...formData, softSkills: e.target.value})}/></div>

                                        {/* PROFILE IMAGE */}
                                        <div className="col-12">
                                            <label className="small text-muted-theme">Profile Picture</label>
                                            <input type="file" accept="image/*" className="form-control" onChange={handleProfileImageChange} />
                                        </div>
                                    </div>
                                </>
                              )}

                              {/* --- TAB: SCORECARD --- */}
                              {activeModalTab === 'scorecard' && (
                                  <div className="p-3">
                                      <h6 className="text-main mb-3">New Interview Scorecard</h6>
                                      <div className="bg-input p-3 border border-theme rounded mb-4">
                                          <div className="row g-3">
                                              <div className="col-md-6">
                                                  <label className="form-label small text-muted-theme">Technical Skills (1-5)</label>
                                                  <input type="number" className="form-control" min="1" max="5" value={newScorecard.technical} onChange={e => setNewScorecard({...newScorecard, technical: e.target.value})} />
                                              </div>
                                              <div className="col-md-6">
                                                  <label className="form-label small text-muted-theme">Culture Fit (1-5)</label>
                                                  <input type="number" className="form-control" min="1" max="5" value={newScorecard.culture} onChange={e => setNewScorecard({...newScorecard, culture: e.target.value})} />
                                              </div>
                                              <div className="col-12">
                                                  <label className="form-label small text-muted-theme">Interview Notes</label>
                                                  <textarea className="form-control" rows="3" value={newScorecard.notes} onChange={e => setNewScorecard({...newScorecard, notes: e.target.value})} placeholder="Strengths, weaknesses, overall impression..."></textarea>
                                              </div>
                                              <div className="col-12 text-end">
                                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAddScorecard}>
                                                      <i className="bi bi-plus-lg me-1"></i> Add Scorecard
                                                  </button>
                                              </div>
                                          </div>
                                      </div>

                                      <h6 className="text-muted-theme">History ({formData.scorecards ? formData.scorecards.length : 0})</h6>
                                      <div className="list-group">
                                          {formData.scorecards && formData.scorecards.length > 0 ? formData.scorecards.map((sc, idx) => (
                                              <div key={idx} className="list-group-item bg-card border-theme">
                                                  <div className="d-flex justify-content-between">
                                                      <small className="fw-bold text-main">{new Date(sc.date).toLocaleDateString()}</small>
                                                      <small className="text-muted-theme">{sc.interviewer}</small>
                                                  </div>
                                                  <div className="my-1">
                                                      <span className="badge bg-primary me-2">Tech: {sc.technical}</span>
                                                      <span className="badge bg-info text-dark">Culture: {sc.culture}</span>
                                                  </div>
                                                  <p className="mb-0 small text-muted-theme">{sc.notes}</p>
                                              </div>
                                          )) : (
                                              <p className="small text-muted-theme fst-italic">No scorecards added yet.</p>
                                          )}
                                      </div>
                                  </div>
                              )}

                              {/* ACTIONS */}
                              <div className="modal-footer px-0 pb-0 mt-4 border-theme d-flex justify-content-between">
                                  {isEditing && <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>Delete</button>}
                                  <div className="d-flex gap-2 ms-auto">
                                      <button type="button" className="btn btn-outline-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                                      <button type="submit" className="btn btn-success" disabled={isUploading}>{isUploading ? 'Saving...' : 'Save Profile'}</button>
                                  </div>
                              </div>
                          </form>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}